import { z } from 'zod';
import { SoundAPIGraphQLError, MissingApiKey, UnexpectedApiResponse } from './errors.mjs';
import { Test, ReleaseInfo, ReleaseShareInfo, MerkleProof, EditionOwnedTokenIds } from './api/graphql/gql.mjs';
import { isHexList } from './utils/helpers.mjs';

const graphqlRequestBody = z.object({
  data: z.record(z.unknown()).nullable().optional(),
  errors: z.array(
    z.object({
      message: z.string(),
      locations: z.array(z.object({ line: z.number(), column: z.number() })).optional(),
      path: z.array(z.union([z.string(), z.number()])).optional(),
      extensions: z.record(z.unknown()).optional()
    })
  ).optional(),
  extensions: z.record(z.unknown()).optional()
});
const CLIENT_KEY_HEADER = "x-sound-client-key";
function SoundAPI({ apiEndpoint = "https://api.sound.xyz/graphql", apiKey }) {
  const apiUrl = new URL(apiEndpoint);
  function graphqlRequest({ query, variables }) {
    if (!apiKey)
      throw new MissingApiKey();
    return fetch(apiUrl, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        [CLIENT_KEY_HEADER]: apiKey
      },
      body: JSON.stringify(
        variables ? {
          query,
          variables
        } : {
          query
        }
      ),
      mode: "cors"
    }).then(
      (response) => response.json().then(
        // @ts-expect-error
        graphqlRequestBody.parse
      )
    ).catch((error) => {
      throw new UnexpectedApiResponse({ error });
    });
  }
  return {
    async check() {
      const response = await graphqlRequest({
        query: Test
      });
      return response.errors ? { errors: response.errors } : null;
    },
    releaseInfo({ contractAddress, editionId = null }) {
      return graphqlRequest({
        query: ReleaseInfo,
        variables: {
          contractAddress,
          editionId
        }
      });
    },
    releaseShareInfo({
      contractAddress,
      editionId = null,
      releaseEmbedUriInput,
      releaseWebappUriInput
    }) {
      return graphqlRequest({
        query: ReleaseShareInfo,
        variables: {
          contractAddress,
          editionId,
          releaseEmbedUriInput: releaseEmbedUriInput ?? void 0,
          releaseWebappUriInput: releaseWebappUriInput ?? void 0
        }
      });
    },
    async merkleProof({ merkleRoot, userAddress }) {
      const { data, errors } = await graphqlRequest({
        query: MerkleProof,
        variables: {
          root: merkleRoot,
          unhashedLeaf: userAddress
        }
      });
      if (errors)
        throw new SoundAPIGraphQLError({ graphqlErrors: errors });
      if (!data?.merkleTreeProof || !isHexList(data.merkleTreeProof.proof))
        return null;
      return data.merkleTreeProof.proof;
    },
    editionOwnedTokenIds(input) {
      return graphqlRequest({
        query: EditionOwnedTokenIds,
        variables: {
          input
        }
      });
    }
  };
}

export { SoundAPI };
